import { useEffect } from 'react';
import { useFeatureFlagEnabled } from '@frameio/feature-flags';
import { groupByDateFormatted } from '@frameio/shared/utils/groupByDateFormatted';
import { Box, Spinner, Text } from '@frameio/vapor';
import { CrossFadeWithDelay } from '../../animations';
import { useDeleteTransferBatches } from '../../hooks';
import { useJobRemove } from '../../hooks/events/useJobRemove';
import { useGetTransferBatches } from '../../hooks/useGetTransferBatches';
import { useUploaderWorker } from '../../hooks/useUploaderWorker';
import type { UploadJobEvent } from '../../types/uploader';
import {
  getIncompleteJobs,
  getTransferBatchHasLocalData,
  isInTerminalState,
} from '../../utils';
import { JobCard } from '../JobCard';
import { NoUploads } from '../NoUploads';
import { TotalProgressBar } from '../TotalProgressBar';
import { TransfersSheetBase } from '../TransfersSheetBase';
import { UploaderSheetList } from '../UploaderSheetList/UploaderSheetList';
import TEXT from './generated/TEXT/UploaderSheet';

TEXT.DEFINE({
  /** Aria label for spinner while transfer batches are loading */
  transferBatchesLoading: 'Transfer batches loading',
});

export function UploaderSheet() {
  const { data: batches, loading, error, localJobs } = useGetTransferBatches();
  const uploader = useUploaderWorker();
  const { deleteTransferBatches } = useDeleteTransferBatches();
  const jobRemove = useJobRemove();

  // Run once on mount to subscribe, unsubscribe on unmount.
  useEffect(() => {
    // Safari's state gets stuck so we need to ensure we refetch the data.
    const eventsToUpdateOn: UploadJobEvent[] = [
      'jobCreated',
      'jobCompleted',
      'jobPaused',
      'jobCanceled',
      'jobRemoved',
    ];

    const eventListenerIds = new Map();
    eventsToUpdateOn.forEach((eventName) => {
      const id = uploader.on(eventName, () => localJobs);
      eventListenerIds.set(eventName, id);
    });

    return () => {
      eventListenerIds.forEach((id, eventNameKey: UploadJobEvent) => {
        uploader.off(eventNameKey, id);
      });

      // It is possible that the batches is already undefined or has a length of 0:
      if (!localJobs || localJobs.length === 0) return;

      /**
       * Remove batches from local storage once they are in a "terminal" state
       * this allows us to keep storage clean and memory usage low
       * especially for large uploads.
       */
      localJobs.forEach((batch) => {
        if (batch.status === null || isInTerminalState(batch)) {
          jobRemove({ payload: { job: batch } });
        }
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const vaporListEnabled = useFeatureFlagEnabled('v4.uploads_sheet_vapor_list');

  if (vaporListEnabled) {
    return (
      <UploaderSheetList
        batches={batches ?? undefined}
        error={error}
        loading={loading}
        localJobs={localJobs}
      />
    );
  }

  if (error) {
    return <NoUploads />;
  }

  if (!batches && loading)
    return (
      <CrossFadeWithDelay>
        <Spinner
          aria-label={TEXT.transferBatchesLoading}
          centered={true}
          data-testid="settingsPageNotificationsSpinner"
          isLoading={true}
          size="medium"
        />
      </CrossFadeWithDelay>
    );

  const groupedBatchesByInsertedAt = groupByDateFormatted(
    batches ?? [],
    'insertedAt'
  );
  const incompleteJobs = getIncompleteJobs(localJobs);
  return batches?.length ? (
    <TransfersSheetBase
      renderFooter={() => <TotalProgressBar incompleteJobs={incompleteJobs} />}
      renderTransfers={({ setFailedItems }) =>
        Object.keys(groupedBatchesByInsertedAt).map((key) => {
          return (
            <div key={key}>
              <Box paddingBottom={8} paddingTop={16}>
                <Text color="secondary" variant="secondary">
                  {key}
                </Text>
              </Box>
              {groupedBatchesByInsertedAt[key].map((job) => {
                const hasLocalData = getTransferBatchHasLocalData(job);
                const onCancelClick = hasLocalData
                  ? uploader.worker.cancelJob
                  : undefined;
                const onForceFailureClick = uploader.worker.forceFailJob;
                const onPauseClick = hasLocalData
                  ? uploader.worker.pauseJob
                  : undefined;
                const onRemoveClick = (id: string, jobId: string) =>
                  deleteTransferBatches([{ id, localJobId: jobId }]);
                const onResumeClick = hasLocalData
                  ? uploader.worker.resumeJob
                  : undefined;
                return (
                  <li key={job.id}>
                    <JobCard
                      job={job}
                      onCancelClick={onCancelClick}
                      onForceFailureClick={onForceFailureClick}
                      onPauseClick={onPauseClick}
                      onRemoveClick={onRemoveClick}
                      onResumeClick={onResumeClick}
                      setFailedItems={setFailedItems}
                    />
                  </li>
                );
              })}
            </div>
          );
        })
      }
      showFooter={!!incompleteJobs.length}
    />
  ) : (
    <NoUploads />
  );
}
